import { Component, Input, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BaseWidgetComponent } from '../../../../../../home/home-widgets/widgets/base-widget.component';
import { AuthService, FilterService } from '../../../../../../_shared/services';
import { FuelKpiRequest } from "../../../../../_shared";
import { formatCost, formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TotalDecreaseService } from '../../../../services/total-decrease.service'
import { isUptrendPositive } from '../../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { DropdownOption, KpiWidgetComponent } from '../../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-total-decrease-widget',
  standalone: true,
  imports: [KpiWidgetComponent, TranslocoPipe],
  templateUrl: './total-decrease-widget.component.html',
  styleUrl: './total-decrease-widget.component.scss',
})
export class TotalDecreaseWidgetComponent extends BaseWidgetComponent {
  @Input() selectedFilter: string;

  private transloco = inject(TranslocoService);

  icon: string = 'assets/icons/icon-total-decrease.svg';
  currencyCode: string = '';
  currencySymbol: string = '';
  hasError: boolean = false;
  errorMessage: string = '';
  dropdownOptions: DropdownOption[] = [
    { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' }
  ];
  selectedUnit = MeasurementTypes.Volume.toString();

  constructor(
    filterService: FilterService,
    private totalDecreaseService: TotalDecreaseService,
    private errorHandlerService: ErrorHandlerService,
    private authService: AuthService
  ) {
    super(filterService);
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  async onFilterChangeAsync(): Promise<void> {
    await this.getTotalDecreaseKpiData();
  }

  async getTotalDecreaseKpiData() {
    this.isLoading = true;
    this.hasError = false;
    if (!this.widgetFilter.accountIds) {
      return
    }

    try {
      const request: FuelKpiRequest = {
        fuelMeasurementType: MeasurementTypes.Volume,
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      this.cancelSubject.next();
      const response = await this.totalDecreaseService.getTotalDecreaseSummary(request, this.cancelSubject);

      if (response.data) {
        this.value = response.data.totalValue;
        this.percentageDifference = response.data.percentageChange;
        this.difference = response.data.difference;
        this.totalCost = response.data.totalCost;
      }

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString);
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  formatPercentChange(value: number | null | undefined): string {
    if (value !== null && value !== undefined) {
      const sign = value >= 0 ? '+' : '';
      return `${sign}${value.toFixed(1)}%`;
    }
    return this.transloco.translate('fuelShared.charts.notAvailable');
  }

  public async onDropdownChanged(selectedOption: DropdownOption) {
    this.selectedUnit = selectedOption.value;
    this.isLoading = true;
    await this.onFilterChangeAsync();
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }
}